import React, { useContext, useEffect, useMemo, useState } from "react";
import BaseIcon from "../BaseIcon";
import { useTranslation } from "react-i18next";
import BaseDivideLine from "../BaseDivideLine";
import useViewController from "./viewController";
import PropTypes from "prop-types";
import { observer } from "mobx-react-lite";
import { MODAL_TYPES } from "../../utils/emitter";
import useModalActions from "../../hooks/useModalActions";
import ExitModal from "../../modals/ExitModal";
import Game from "../../store/Game";
import { action } from "mobx";
import useBrowserStorage from "../../hooks/useBrowserStorage";
import CompleteTestToSeeResults from "../../modals/CompleteTestToSeeResults";
import Points from "../../store/Points";
import "./style.scss";
import PersonalAccount from "../../store/PersonalAccount";
import useAuth from "../../hooks/useAuth";
import ROUTES from "../../utils/routes.js";
import useCurrentUserData from "../../hooks/useCurrentUserData";
import INTOUCH_API from "../../services/Api";
import { useNavigate } from "react-router-dom";
import { shopStore } from "../../store/Shop";
import openExternalUrl from "../../utils/openExternalUrl";
import { getShopHomeUrl, getShopName } from "../../utils/shopProducts";
import trackEvent, { storeNameFromUrl } from "../../utils/analytics";

export default observer(function BurgerMenu({
  isHideInsightEvents,
  iconKeys,
  className,
  onNavigateInsightForm,
}) {
  BurgerMenu.propTypes = {
    iconKeys: PropTypes.arrayOf(PropTypes.string),
    className: PropTypes.string,
    onNavigateInsightForm: PropTypes.func,
  };
  const [isOpen, setIsOpen] = useState();
  const { token } = useAuth();
  const { t, i18n } = useTranslation();
  const { toPath, items, shareCompatibilityPage, startDemoGame } =
    useViewController(iconKeys);
  const navigate = useNavigate();

  // live-бейдж баллов: первичная загрузка; обновления приходят по socket points-accrued
  useEffect(() => {
    if (token && Points.balance === null) {
      Points.fetchBalance();
    }
  }, [token]);
  const { openModal } = useModalActions();
  const { getItem } = useBrowserStorage();
  const game = Game.game;
  const { getUserData } = useCurrentUserData();

  const getGameEvents = async () => {
    await INTOUCH_API.requestManager("get_game_events", null, (response) => {
      Game.setGameEventsData(response.data);
    });
  };

  useEffect(() => {
    setTimeout(() => {
      getGameEvents();
    }, 500);
  }, []);

  const getAction = (typeAction) => {
    switch (typeAction) {
      case "logout":
        return () => openModal(MODAL_TYPES.EXIT);
      case "toProfile":
        return () => {
          token
            ? toPath(ROUTES.PERSONAL_ACCOUNT_PAGE)
            : toPath(ROUTES.REGISTER_PAGE);
          setIsOpen(false);
        };
      case "toPwaInstructions":
        return () => toPath(ROUTES.PWA_INSTRUCTIONS_PAGE);
      case "changeLanguage":
        return () => {
          i18n.changeLanguage(i18n.language === "ru" ? "en" : "ru");
        };
      case "toTechSupport":
        return () => toPath(ROUTES.SUPPORT_PAGE);
      case "toLeaveReview":
        return () => {
          toPath(ROUTES.LEAVE_REVIEW_PAGE);
          setIsOpen(false);
        };
      case "toInGame":
        return () => {
          if (!PersonalAccount.games.length) {
            startDemoGame();
            // openModal(MODAL_TYPES.COMPLETE_TEST_TO_SEE_RESULTS);
            setIsOpen(false);
            return;
          }

          if (game) {
            const isPartnerNotJoined = !game.invitedUserId;
            const hasInviteLink = !!game?.link?.link;

            // Если партнер ещё не присоединился и есть ссылка — показываем модалку с ссылкой
            if (
              isPartnerNotJoined &&
              hasInviteLink &&
              game.answers.length === Game.cards.length
            ) {
              openModal(MODAL_TYPES.LINK_WAS_NOT_SENT);
              setIsOpen(false);
              return;
            }

            const matches = Game.countPartnerGameMatches(game.id);
            if (matches) {
              setIsOpen(false);
              if (!PersonalAccount.hasViewedRules(game.id)) {
                return toPath(ROUTES.ACCEPT_RULES_PAGE);
              }
              return toPath(ROUTES.MATCHES_PAGE);
            } else {
              if (
                game.type === "DEMO" &&
                !PersonalAccount.hasViewedMeet(game.id)
              ) {
                navigate(ROUTES.MEET_PAGE);
                return;
              }
              if (
                !PersonalAccount.hasViewedGreetings(game.id) &&
                game.type !== "DEMO"
              ) {
                setIsOpen(false);
                return toPath(ROUTES.GREETINGS_PAGE);
              }
              setIsOpen(false);
              return toPath(ROUTES.IN_GAME_PAGE);
            }
          }
        };
      case "toRules":
        return () => {
          toPath(ROUTES.RULES_PAGE);
          setIsOpen(false);
        };
      case "toInsightQuestionnaire":
        return onNavigateInsightForm
          ? onNavigateInsightForm()
          : action(() => {
              if (!game) {
                setIsOpen(false);
                return toPath(ROUTES.INSIGHT_DESCRIPTION);
              }
              setIsOpen(false);
              // item 17.09: раздел «Анкета Откровений» выглядит одинаково независимо
              // от того, куплена игра или нет (решение клиента). Прежние гейты —
              // «DEMO → страница продажи» и «инструкция не прочитана → описание» —
              // сняты: продающее описание теперь показывается попапом на «Выбери
              // карточку», когда игра не куплена, а не подменяет собой весь раздел.
              return toPath(ROUTES.INSIGHT_QUESTIONNAIRE_PAGE);
            });
      case "toCompatibilityShare":
        return () => shareCompatibilityPage();
      case "toShop":
        return () => {
          setIsOpen(false);
          // Страна выбрана и магазин для неё есть (сегодня — только RU) → сразу в магазин.
          // Нет страны ИЛИ регион не поддерживается → ShopModal (выбор страны /
          // сообщение "магазин в разработке"), как в самой модалке.
          const url = getShopHomeUrl(shopStore.selectedCountry);
          if (url) {
            // GA4 «Stats v2»: прямой переход в магазин из меню (без карточки)
            trackEvent("store_click", {
              event_category: "Outbound Link",
              card_id: null,
              chosen_country: shopStore.selectedCountry || null,
              store_name:
                getShopName(shopStore.selectedCountry) || storeNameFromUrl(url),
            });
            openExternalUrl(url);
          } else shopStore.open(null);
        };
      default:
        return () => {};
    }
  };

  useEffect(() => {
    if (isOpen) {
      Game.getAllGamesMatches();
    }
  }, [isOpen]);

  const gameNotificationCount = useMemo(() => {
    const user = getUserData();
    if (game && user) {
      const countEndGame = Game.getEndGameNotificationCount(game.id, user.id);
      return countEndGame;
    } else {
      return 0;
    }
  }, [game, Game.endGameEvent]); // ✅ Добавляем Game.endGameEvent

  const allGameNotificationCount = useMemo(() => {
    const user = getUserData();
    if (user) {
      const countEndGames = Game.getAllEndGameNotificationCount(user.id);
      return countEndGames;
    }
    return 0;
  }, [Game.endGameEvent]); // ✅ Добавляем Game.endGameEvent

  const allInsightEvent = useMemo(() => {
    const user = getUserData();
    if (user) {
      const count = Game.getAllInsightEvent(user.id);
      console.log("🔢 BurgerMenu: пересчитан insightEvent:", {
        userId: user.id,
        count,
        insightEvents: Game.insightEvents,
      });
      return count;
    } else {
      return null;
    }
  }, [Game.insightEvents]); // ✅ Добавляем Game.insightEvents в зависимости

  // Бейдж пункта «Insight Form»: insight-события текущей игры. Раньше брался из
  // Game.currentGameInsightEventData, который заполняется только если Game.game
  // был установлен К МОМЕНТУ прихода данных — при свежем заходе (Game.game ещё
  // null) бейдж у пункта не появлялся вовсе (весь счёт уходил в «Личный
  // кабинет»). Считаем напрямую из insightEvents по id текущей игры с фолбэком
  // на localStorage.
  const insightFormBadge = useMemo(() => {
    const user = getUserData();
    if (!user) return 0;
    let gid = Game.game?.id;
    if (!gid) {
      try {
        gid = JSON.parse(localStorage.getItem("currentGame") || "null")?.id;
      } catch (e) {
        gid = null;
      }
    }
    if (!gid) return 0;
    const event = Game.getInsightEvent(gid, user.id);
    return event?.count || 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Game.insightEvents, Game.game]);

  const differenceNotifications = useMemo(() => {
    if (
      allInsightEvent > 0 &&
      Game.currentGameInsightEventData &&
      Game.currentGameInsightEventData.count
    ) {
      return (
        allInsightEvent +
        allGameNotificationCount -
        (Game.currentGameInsightEventData.count + gameNotificationCount)
      );
    } else if (allInsightEvent > 0) {
      return allInsightEvent + allGameNotificationCount - gameNotificationCount;
    } else if (allGameNotificationCount > 0) {
      return allGameNotificationCount - gameNotificationCount;
    } else {
      return null;
    }
  }, [
    allInsightEvent,
    allGameNotificationCount,
    gameNotificationCount, // ✅ Добавляем для отслеживания изменений
  ]);

  useEffect(() => {
    if ("setAppBadge" in navigator) {
      navigator
        .setAppBadge(allInsightEvent + allGameNotificationCount)
        .catch((error) => {});
    }
  }, [allInsightEvent, allGameNotificationCount]);

  return (
    <div className={`burger-menu-wrapper ${className}`}>
      <ExitModal />
      <CompleteTestToSeeResults />
      {!isHideInsightEvents && allInsightEvent > 0 ? (
        <div className="menu-wrapper__item__count menu-wrapper__item__count--burger">
          {allInsightEvent + allGameNotificationCount}
        </div>
      ) : null}
      {!isHideInsightEvents &&
      allInsightEvent === 0 &&
      allGameNotificationCount > 0 ? (
        <div className="menu-wrapper__item__count menu-wrapper__item__count--burger">
          {allGameNotificationCount}
        </div>
      ) : null}
      <div className="burger-menu-button" onClick={() => setIsOpen(!isOpen)}>
        <div className="burger-menu-button__line burger-menu-button__line-1" />
        <div className="burger-menu-button__line burger-menu-button__line-2" />
        <div className="burger-menu-button__line burger-menu-button__line-3" />
      </div>
      {isOpen ? (
        <>
          <div
            onClick={() => setIsOpen(false)}
            id="modal-backdrop"
            className="burger-menu-backdrop"
          />
          <div className="menu-container">
            <div className="menu-wrapper">
              {Points.balance !== null ? (
                // Баллы лояльности (ТЗ п.6.2): некликабельный live-бейдж над пунктами меню
                // (дизайн Figma: иконка-звезда слева, шрифт как в консоли)
                <div className="menu-wrapper__points">
                  <BaseIcon
                    name="star"
                    className="menu-wrapper__points-star"
                  />
                  <span className="menu-wrapper__points-value">
                    {Points.balance}
                  </span>
                  <span className="menu-wrapper__points-label">
                    {t("loyalty.menu_points_label")}
                  </span>
                </div>
              ) : null}
              <div className="menu-wrapper__top">
                {items.top.map((value, index) => {
                  return (
                    <div
                      key={index}
                      onClick={getAction(value.action)}
                      className="menu-wrapper__item"
                    >
                      <div className="menu-wrapper__item__count-wrapper">
                        <div className="menu-wrapper__item--icon-wrapper">
                          <BaseIcon name={value.icon} />
                        </div>
                        {value.action === "toProfile" &&
                        !isHideInsightEvents &&
                        differenceNotifications ? (
                          <div className="menu-wrapper__item__count">
                            {differenceNotifications}
                          </div>
                        ) : null}
                        {value.action === "toInsightQuestionnaire" &&
                        !isHideInsightEvents &&
                        insightFormBadge > 0 ? (
                          <div className="menu-wrapper__item__count">
                            {insightFormBadge}
                          </div>
                        ) : null}
                        {value.action === "toInGame" &&
                        gameNotificationCount > 0 ? (
                          <div className="menu-wrapper__item__count">
                            {gameNotificationCount}
                          </div>
                        ) : null}
                      </div>
                      <div className="menu-wrapper__item-text">
                        {t("burger_menu." + value.tKey)}
                      </div>
                    </div>
                  );
                })}
              </div>
              <BaseDivideLine className="menu-wrapper__dash" dash />
              <div className="menu-wrapper__bottom">
                {items.bottom.map((value, index) => {
                  if (value.action === "logout" && !token) {
                    return null;
                  }
                  if (value.action === "toLeaveReview" && !token) {
                    return null;
                  }
                  return (
                    <div
                      key={index}
                      onClick={getAction(value.action)}
                      className="menu-wrapper__item"
                    >
                      <div className="menu-wrapper__item--icon-wrapper">
                        <BaseIcon name={value.icon} />
                      </div>
                      <div className="menu-wrapper__item-text">
                        {t("burger_menu." + value.tKey)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
});
